/**
 * The scope picker. The scope offers post, account and campaign window only;
 * segment and territory are never scopes of this series (`./scopes.ts`). The
 * reading window is `WindowSelect`'s, shared with Território rather than
 * kept here.
 */

import type { ReactNode } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import type { PostRow } from '../api/schemas';
import { st2 } from './messages';
import { SCOPE_KINDS, accountsOf, type ScopeKind } from './scopes';

export function ScopePicker(props: {
  kind: ScopeKind;
  onKindChange: (kind: ScopeKind) => void;
  scopeRef: string;
  onScopeRefChange: (ref: string) => void;
  posts: readonly PostRow[];
}): ReactNode {
  const options =
    props.kind === 'account'
      ? accountsOf(props.posts).map((account) => ({
          id: account.id,
          label: account.label ?? st2('scope.account'),
        }))
      : props.posts.map((post) => ({
          id: post.post_id,
          label: post.caption ?? post.handle ?? post.label ?? st2('scope.post'),
        }));

  return (
    <div
      role="group"
      aria-label={st2('filters.label')}
      className="flex flex-wrap items-center gap-2"
    >
      <Select value={props.kind} onValueChange={(kind) => props.onKindChange(kind as ScopeKind)}>
        <SelectTrigger aria-label={st2('scope.kind')}>
          <SelectValue placeholder={st2('scope.kind')} />
        </SelectTrigger>
        <SelectContent>
          {SCOPE_KINDS.map((kind) => (
            <SelectItem key={kind} value={kind}>
              {st2(`scope.${kind}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {props.kind !== 'campaign' && (
        <Select value={props.scopeRef} onValueChange={props.onScopeRefChange}>
          <SelectTrigger aria-label={st2(`scope.ref.${props.kind}`)} className="max-w-64">
            <SelectValue placeholder={st2(`scope.ref.${props.kind}`)} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
